import { LeadTimeMutationError } from '../components/LeadTimeMutationError';
import { useState } from 'react';
import { useGT } from 'gt-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FloppyDiskIcon,
  XIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import {
  type LeadTimeComponent,
  type LeadTimeComponentOverride,
  type LeadTimeComponentSource,
} from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LeadTimeComponentSourceInput } from '../components/LeadTimeComponentSourceInput';

import {
  activeComponentOverride,
  overrideCanRevoke,
  overrideFormValid,
  overrideLifecycle,
  overrideWindow,
  overrideDraftDefaults,
} from '../components/leadTimeComponentForm';

function ComponentOverrideEditor({
  component,
  current,
  productV3Key,
  warehouseId,
  onSaved,
  onNotice,
}: {
  component: LeadTimeComponent;
  current?: LeadTimeComponentOverride;
  productV3Key: string;
  warehouseId: string;
  onSaved: () => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const gt = useGT();
  const initial = overrideDraftDefaults(component, current);
  const [source, setSource] = useState<LeadTimeComponentSource>(initial.source);
  const [reason, setReason] = useState('');
  const [expiryDate, setExpiryDate] = useState(initial.expiryDate);
  const [startsDate, setStartsDate] = useState('');
  const [reviewDate, setReviewDate] = useState(initial.reviewDate);
  const save = useMutation({
    ...orpc.procurement.leadTimeSettings.saveOverride.mutationOptions(),
    onSuccess: async () => {
      onNotice(
        gt(
          'Saved. Run demand forecasting, then replenishment, to update quantities and estimates.',
          { context: 'component lead time override saved stale results' },
        ),
      );
      await onSaved();
    },
  });
  const revoke = useMutation({
    ...orpc.procurement.leadTimeSettings.revokeOverride.mutationOptions(),
    onSuccess: async () => {
      onNotice(
        gt(
          'Inheritance restored for future calculations. Existing results need regeneration.',
          { context: 'component lead time override clear saved stale results' },
        ),
      );
      await onSaved();
    },
  });
  const pending = save.isPending || revoke.isPending;
  return (
    <div className="space-y-2 border-t py-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{component.label}</span>
        <span className="text-muted-foreground">
          {current
            ? gt('SKU / warehouse override', {
                context: 'component lead time scoped owner',
              })
            : gt('Inherits organization', {
                context: 'component lead time source inherited',
              })}
        </span>
      </div>
      <LeadTimeComponentSourceInput
        component={component}
        source={source}
        onChange={setSource}
      />
      <Label>
        {gt('Reason', { context: 'component lead time override audit reason' })}
      </Label>
      <Input
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      {current && (
        <p className="text-xs text-muted-foreground">
          {gt(
            'Saving creates a replacement starting now. Expiry and review dates below apply to the replacement.',
            { context: 'component override replacement lifecycle explanation' },
          )}
        </p>
      )}
      <Label>
        {gt('Starts on (UTC, optional)', {
          context: 'lead time component override scheduled start date',
        })}
      </Label>
      <Input
        type="date"
        disabled={Boolean(current)}
        value={startsDate}
        onChange={(event) => setStartsDate(event.target.value)}
      />
      <Label>
        {gt('Expires on (UTC, optional)', {
          context: 'lead time component override exclusive expiry date',
        })}
      </Label>
      <Input
        type="date"
        value={expiryDate}
        onChange={(event) => setExpiryDate(event.target.value)}
      />
      <Label>
        {gt('Review date', {
          context: 'open ended component lead time override review date',
        })}
      </Label>
      <Input
        type="date"
        value={reviewDate}
        onChange={(event) => setReviewDate(event.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={
            pending ||
            !overrideFormValid(source, reason, reviewDate, expiryDate)
          }
          onClick={() =>
            save.mutate({
              componentId: component.id,
              productV3Key,
              warehouseId,
              source,
              reason,
              ...overrideWindow(current, startsDate, expiryDate, reviewDate),
            })
          }
        >
          <FloppyDiskIcon className="h-4 w-4" />
          {gt('Save Component', {
            context: 'save lead time component override button',
          })}
        </Button>
        {current && (
          <Button
            variant="outline"
            size="sm"
            disabled={pending}
            onClick={() => revoke.mutate({ overrideId: current.id })}
          >
            <XIcon className="h-4 w-4" />
            {gt('Restore Inheritance', {
              context: 'clear lead time component source override button',
            })}
          </Button>
        )}
      </div>
      <LeadTimeMutationError errors={[save.error, revoke.error]} />
    </div>
  );
}

function OverrideStatus({
  status,
}: {
  status: ReturnType<typeof overrideLifecycle>;
}) {
  const gt = useGT();
  const labels = {
    revoked: gt('Revoked', { context: 'lead time component history status' }),
    expired: gt('Expired', { context: 'lead time component history status' }),
    scheduled: gt('Scheduled', {
      context: 'lead time component history status',
    }),
    review_overdue: gt('Active · Review Overdue', {
      context: 'lead time component history status',
    }),
    active: gt('Active', { context: 'lead time component history status' }),
  };
  return labels[status];
}

function OverrideHistoryRow({
  row,
  label,
  now,
  pending,
  onRevoke,
}: {
  row: LeadTimeComponentOverride;
  label: string;
  now: number;
  pending: boolean;
  onRevoke: (id: string) => void;
}) {
  const gt = useGT();
  return (
    <p>
      {label} ·{' '}
      {row.source.kind === 'static'
        ? gt('{days}d', {
            context: 'lead time component audit history static days',
            days: row.source.days,
          })
        : gt('Calculated', {
            context: 'lead time component audit history source',
          })}{' '}
      · {row.createdAt} · {row.createdByUserId} · {row.reason} ·{' '}
      <OverrideStatus status={overrideLifecycle(row, now)} />
      {' · '}
      {gt('Starts: {date}', {
        context: 'lead time override history start',
        date: row.startsAt.slice(0, 10),
      })}
      {' · '}
      {row.endsAt
        ? gt('Expires: {date}', {
            context: 'lead time override history expiry',
            date: row.endsAt.slice(0, 10),
          })
        : gt('No expiry', {
            context: 'lead time override history open ended',
          })}
      {row.reviewAt && (
        <>
          {' · '}
          {gt('Review: {date}', {
            context: 'lead time override history review',
            date: row.reviewAt.slice(0, 10),
          })}
        </>
      )}
      {overrideCanRevoke(row, now) && (
        <Button
          variant="ghost"
          size="sm"
          disabled={pending}
          onClick={() => onRevoke(row.id)}
        >
          <XIcon className="h-4 w-4" />
          {gt('Revoke', {
            context:
              'revoke active or scheduled lead time component override history action',
          })}
        </Button>
      )}
    </p>
  );
}

export function LeadTimeComponentOverridePanel({
  productV3Key,
  warehouseId,
}: {
  productV3Key: string;
  warehouseId: string;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const settings = useQuery(
    orpc.procurement.leadTimeSettings.get.queryOptions(),
  );
  const history = useQuery(
    orpc.procurement.leadTimeSettings.listOverrides.queryOptions({
      input: { productV3Key, warehouseId },
    }),
  );
  const profile = settings.data?.profile;
  const revokeHistory = useMutation({
    ...orpc.procurement.leadTimeSettings.revokeOverride.mutationOptions(),
    onSuccess: onSaved,
  });
  async function onSaved() {
    await queryClient.invalidateQueries({
      queryKey: orpc.procurement.leadTimeSettings.key(),
    });
    await queryClient.invalidateQueries({
      queryKey: orpc.procurement.leadTimes.key(),
    });
  }
  if (settings.isError || history.isError)
    return (
      <p className="text-xs text-destructive">
        {gt('Could not read component settings.', {
          context: 'component lead time override read error',
        })}
      </p>
    );
  const now = Math.max(settings.dataUpdatedAt, history.dataUpdatedAt);
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-medium">
        {gt('Component overrides', {
          context: 'sku warehouse lead time component source overrides heading',
        })}
      </h4>
      <p className="text-xs text-muted-foreground">
        {gt(
          'Each component inherits its organization source unless overridden here. A total lead time override supersedes all components. Calendar days; expiry or review is required.',
          { context: 'lead time component override precedence explanation' },
        )}
      </p>
      {!profile && (
        <p className="text-xs text-muted-foreground">
          {gt(
            'The organization profile is inactive. Retained overrides can be revoked below before reactivation.',
            {
              context: 'inactive lead time profile retained component history',
            },
          )}
        </p>
      )}
      {profile?.components.map((component) => {
        const current = activeComponentOverride(
          history.data?.overrides ?? [],
          component.id,
          now,
        );
        return (
          <ComponentOverrideEditor
            key={`${component.id}:${current?.id ?? settings.data?.fingerprint}`}
            component={component}
            current={current}
            productV3Key={productV3Key}
            warehouseId={warehouseId}
            onSaved={onSaved}
            onNotice={setMessage}
          />
        );
      })}
      {message && (
        <p role="status" className="text-xs text-amber-700">
          {message}
        </p>
      )}
      {revokeHistory.error && (
        <p role="alert" className="text-xs text-destructive">
          {revokeHistory.error.message}
        </p>
      )}
      <details className="text-xs">
        <summary>
          {gt('Component override history', {
            context: 'lead time component override audit history disclosure',
          })}
        </summary>
        {history.data?.overrides.map((row) => (
          <OverrideHistoryRow
            key={row.id}
            row={row}
            label={
              profile?.components.find(
                (component) => component.id === row.componentId,
              )?.label ?? row.componentId
            }
            now={now}
            pending={revokeHistory.isPending}
            onRevoke={(id) => revokeHistory.mutate({ overrideId: id })}
          />
        ))}
      </details>
      <Button variant="outline" size="sm" asChild>
        <a href="/workflows">
          <ArrowSquareOutIcon className="h-4 w-4" />
          {gt('Open Workflows', {
            context: 'lead time component override workflow regeneration link',
          })}
        </a>
      </Button>
    </section>
  );
}
